import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash, Buildings } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import UserMultiSelect from "../components/UserMultiSelect";
import TicketCountRow from "../components/TicketCountRow";
import { formatDate } from "../lib/utils";

const BUCKET_COLS = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "pending_client", label: "Pending client" },
  { key: "completed", label: "Completed" },
];

function BucketCountCell({ orgId, bucketKey, count }) {
  return (
    <td className="text-center">
      <Link
        to={`/tickets?organization_id=${orgId}&bucket=${bucketKey}`}
        className="inline-block min-w-[1.5rem] text-slate-700 hover:text-blue-600 hover:underline font-medium"
        data-testid={`org-${orgId}-${bucketKey}-count`}
      >
        {count ?? 0}
      </Link>
    </td>
  );
}

export default function Organizations() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact_email: "", phone: "", assign_user_ids: [] });
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const canManage = user.role === "super_admin";

  const refresh = () => api.get("/organizations").then((r) => setOrgs(r.data)).finally(() => setLoaded(true));
  useEffect(() => {
    refresh();
    if (canManage) api.get("/users").then((r) => setAssignable(r.data.filter((u) => !["super_admin", "project_manager"].includes(u.role))));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/organizations", form);
      setOpen(false);
      setForm({ name: "", contact_email: "", phone: "", assign_user_ids: [] });
      refresh();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    try {
      await api.delete(`/organizations/${id}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="space-y-5" data-testid="organizations-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Directory</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Clients</h1>
          <p className="text-sm text-slate-500 mt-1">Organizations you support and their associated projects.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-org-button">
                <Plus size={14} weight="bold" className="mr-1.5" /> New client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create client</DialogTitle></DialogHeader>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="org-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Organization name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Contact email</Label>
                  <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="org-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="org-phone-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Assign users (optional)</Label>
                  <UserMultiSelect users={assignable} value={form.assign_user_ids}
                    onChange={(ids) => setForm({ ...form, assign_user_ids: ids })}
                    placeholder="Give users access to this client…" />
                  <p className="text-[11px] text-slate-400">Selected users get access to all of this client's projects.</p>
                </div>
                {error && <div className="text-xs text-rose-600">{error}</div>}
                <DialogFooter>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="org-submit-button">Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Table — md and above */}
      <div className="hidden md:block surface-card overflow-x-auto">
        <table className="w-full text-sm table-tight" data-testid="orgs-table">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-[0.1em] text-slate-500">
            <tr>
              <th>Client</th>
              <th>Contact email</th>
              <th className="text-center">Projects</th>
              {BUCKET_COLS.map((b) => <th key={b.key} className="text-center">{b.label}</th>)}
              <th>Added</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!loaded && <tr><td colSpan="9" className="text-center text-slate-400 py-10">Loading clients…</td></tr>}
            {loaded && orgs.length === 0 && <tr><td colSpan="9" className="text-center text-slate-400 py-10">No clients yet.</td></tr>}
            {orgs.map((o) => (
              <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`org-row-${o.id}`}>
                <td>
                  <Link to={`/organizations/${o.id}`} className="flex items-center gap-2.5 hover:underline">
                    <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center"><Buildings size={16} /></div>
                    <span className="font-medium text-slate-800">{o.name}</span>
                  </Link>
                </td>
                <td className="text-slate-600">{o.contact_email || "—"}</td>
                <td className="text-center text-slate-600">{o.projects_count ?? 0}</td>
                {BUCKET_COLS.map((b) => (
                  <BucketCountCell key={b.key} orgId={o.id} bucketKey={b.key} count={o.ticket_counts?.[b.key]} />
                ))}
                <td className="text-xs text-slate-500">{formatDate(o.created_at)}</td>
                <td className="text-right">
                  {canManage && (
                    <button onClick={() => remove(o.id)} className="text-slate-400 hover:text-rose-600" data-testid={`org-delete-${o.id}`}>
                      <Trash size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards — mobile only (below md) */}
      <div className="md:hidden space-y-2">
        {!loaded && (
          <div className="text-center text-slate-400 py-10 surface-card">Loading clients…</div>
        )}
        {loaded && orgs.length === 0 && (
          <div className="text-center text-slate-400 py-10 surface-card">No clients yet.</div>
        )}
        {orgs.map((o) => (
          <div key={o.id} className="surface-card p-4" data-testid={`org-card-${o.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center shrink-0 mt-0.5">
                  <Buildings size={16} />
                </div>
                <div className="min-w-0">
                  <Link to={`/organizations/${o.id}`} className="font-medium text-slate-800 truncate hover:underline block">{o.name}</Link>
                  <div className="text-xs text-slate-500 mt-0.5">{o.contact_email || "—"}</div>
                  <div className="text-xs text-slate-500">{o.phone || "—"}</div>
                  <div className="text-xs text-slate-400 mt-1">{o.projects_count ?? 0} project{o.projects_count === 1 ? "" : "s"} · Added {formatDate(o.created_at)}</div>
                </div>
              </div>
              {canManage && (
                <button type="button" onClick={() => remove(o.id)} className="text-slate-400 hover:text-rose-600 shrink-0" data-testid={`org-card-delete-${o.id}`}>
                  <Trash size={16} />
                </button>
              )}
            </div>
            <TicketCountRow organizationId={o.id} counts={o.ticket_counts} className="mt-3" />
          </div>
        ))}
      </div>
    </div>
  );
}
